// ============================================================
// ✅ LiaisonsTab.jsx - Gestion des liaisons TypeConge ↔ Règle
// ============================================================

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { Plus, Trash2, RefreshCw, User, Building2, Globe } from 'lucide-react';

export default function LiaisonsTab() {
  const { user } = useUser();
  const [liaisons, setLiaisons] = useState([]);
  const [regles, setRegles] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [typesConge, setTypesConge] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ ID_TypeConge: '', ID_Regle: '', ID_Societe: '', ID_Employe: '' });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [liaisonsRes, reglesRes, empRes, typesRes] = await Promise.all([
        api.get('/regles/liaisons', { params: { ID_Societe: user?.ID_Societe || null } }),
        api.get('/regles'),
        api.get('/employes', { params: { ID_Societe: user?.ID_Societe || null } }),
        api.get('/typesconge')
      ]);
      setLiaisons(liaisonsRes.data.liaisons || []);
      setRegles(reglesRes.data.regles || []);
      setEmployes(empRes.data.employes || []);
      setTypesConge(typesRes.data.typesconge || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDelete = async (liaison) => {
    if (!window.confirm('Supprimer cette liaison ?')) return;
    try {
      await api.delete('/regles/liaisons', { data: { ID_TypeConge: liaison.ID_TypeConge, ID_Regle: liaison.ID_Regle, ID_Societe: liaison.ID_Societe } });
      loadAll();
    } catch (err) { alert('Erreur'); }
  };

  const handleAdd = async () => {
    if (!form.ID_TypeConge || !form.ID_Regle) { alert('Type et règle requis'); return; }
    try {
      await api.post('/regles/liaisons', { ...form, ID_Societe: form.ID_Societe || null, ID_Employe: form.ID_Employe || null });
      setShowModal(false);
      setForm({ ID_TypeConge: '', ID_Regle: '', ID_Societe: '', ID_Employe: '' });
      loadAll();
    } catch (err) { alert(err.response?.data?.message || 'Erreur'); }
  };

  const getNiveauBadge = (l) => {
    if (l.ID_Employe) return { icon: <User size={10} />, label: employes.find(e => e.ID_Employes === l.ID_Employe)?.Prenom || 'Emp', bg: 'bg-blue-100 text-blue-700' };
    if (l.ID_Societe) return { icon: <Building2 size={10} />, label: l.SocieteNom || 'Soc', bg: 'bg-amber-100 text-amber-700' };
    return { icon: <Globe size={10} />, label: '🌍 National', bg: 'bg-green-100 text-green-700' };
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Chargement...</div>;

  return (
    <>
      <div className="flex justify-between mb-3">
        <p className="text-xs text-gray-500">{liaisons.length} liaison(s)</p>
        <button onClick={() => setShowModal(true)} className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs flex items-center gap-1">
          <Plus size={12} /> Ajouter
        </button>
        <button onClick={loadAll} className="p-2 text-gray-400 hover:text-emerald-600"><RefreshCw size={16} /></button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50"><tr className="text-[11px] text-gray-500">
              <th className="px-3 py-2 text-left">Type congé</th><th className="px-3 py-2 text-left">Règle</th>
              <th className="px-3 py-2 text-center">Valeur</th><th className="px-3 py-2 text-center">Niveau</th>
              <th className="px-3 py-2 text-center">Version</th><th className="px-3 py-2 text-center">Actions</th>
            </tr></thead>
            <tbody className="divide-y">
              {liaisons.length === 0 ? (
                <tr><td colSpan="6" className="px-3 py-8 text-center text-gray-400">Aucune liaison</td></tr>
              ) : liaisons.map((l, i) => {
                const n = getNiveauBadge(l);
                return (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{l.CodeConge} - {l.LibelleConge}</td>
                    <td className="px-3 py-2">{l.LibelleRegle}</td>
                    <td className="px-3 py-2 text-center font-bold">{l.Valeur}</td>
                    <td className="px-3 py-2 text-center"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] ${n.bg}`}>{n.icon} {n.label}</span></td>
                    <td className="px-3 py-2 text-center">v{l.VersionNumber || 1}</td>
                    <td className="px-3 py-2 text-center"><button onClick={() => handleDelete(l)} className="p-1 text-red-400 hover:text-red-600"><Trash2 size={14} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">➕ Nouvelle liaison</h3>
            <div className="space-y-3">
              <select className="w-full p-2 border rounded-lg text-sm" value={form.ID_TypeConge} onChange={e => setForm({...form, ID_TypeConge: e.target.value})}>
                <option value="">Type de congé *</option>
                {typesConge.map(t => <option key={t.ID_TypeConge} value={t.ID_TypeConge}>{t.Code} - {t.Libelle}</option>)}
              </select>
              <select className="w-full p-2 border rounded-lg text-sm" value={form.ID_Regle} onChange={e => setForm({...form, ID_Regle: e.target.value})}>
                <option value="">Règle *</option>
                {regles.map(r => <option key={r.ID_Regle} value={r.ID_Regle}>{r.Code} - {r.Libelle}</option>)}
              </select>
              <select className="w-full p-2 border rounded-lg text-sm" value={form.ID_Employe || ''} onChange={e => setForm({...form, ID_Employe: e.target.value || null})}>
                <option value="">👤 Employé (optionnel)</option>
                {employes.map(e => <option key={e.ID_Employes} value={e.ID_Employes}>{e.Prenom} {e.Nom}</option>)}
              </select>
              {!form.ID_Employe && (
                <select className="w-full p-2 border rounded-lg text-sm" value={form.ID_Societe || ''} onChange={e => setForm({...form, ID_Societe: e.target.value || null})}>
                  <option value="">🏢 Société (optionnel)</option>
                  <option value={user?.ID_Societe || 1}>{user?.SocieteNom || 'Société'}</option>
                </select>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 bg-gray-100 rounded-lg text-sm">Annuler</button>
                <button onClick={handleAdd} className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm">Ajouter</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}